import useUserPermission from "~/hooks/useUserPermission";
import { useConversationContext } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import {
  NEXTGEN_CHAT_EVENT,
  trackNextgenChatEvent,
  type NextgenChatEventContext,
} from "~/pages/manifold-nextgen/components/conversations/analytics";
import {
  artifactDownloadPath,
  artifactExportPath,
  artifactMethodsDocumentPath,
  artifactMethodsProjectFilesPath,
  artifactProjectFilesPath,
  type ArtifactDownloadResult,
  type ArtifactExportResult,
  type ArtifactMethodsDocument,
  type ArtifactType,
  type ConvertAndCopyFormat,
  type MethodsDocumentFormat,
  type ProjectFile,
} from "~/services/artifacts";
import {
  pinnedArtifactDownloadPath,
  pinnedArtifactMethodsDocumentPath,
  type PinRef,
} from "~/services/pinnedArtifacts";
import { apiClient, apiMutate } from "~/utils/apiClient";
import { methodsValueFor } from "./formats";

const METHODS_CONTENT_TYPES: Record<MethodsDocumentFormat, string> = {
  markdown: "text/markdown",
  notebook: "application/x-ipynb+json",
  quarto: "text/markdown",
};

export type DownloadResult = { ok: true } | { ok: false; error: string };

const saveBlob = (blob: Blob, fileName: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
};

export type ExportResult =
  | { ok: true; result: ArtifactExportResult }
  | { ok: false; error: string };

export type SaveResult = { ok: true; file: ProjectFile } | { ok: false; error: string };

/** Whether anything here can download: a session or a pin is in scope. */
export const useCanDownload = (): boolean => {
  const { conversationId, pin } = useConversationContext();
  return Boolean(pin) || conversationId !== null;
};

// Downloading an artifact's data takes the data downloader role's scope, which
// the download routes enforce. The methods document is a write-up, not data,
// and needs none.
const DOWNLOAD_DATA_PERMISSION = "create:file-browser-download";

/** Whether the caller may download an artifact's data, as opposed to its methods document. */
export const useCanDownloadData = (): boolean => useUserPermission(DOWNLOAD_DATA_PERMISSION);

// Per-artifact mutating actions: download (fetch presigned URL + trigger
// browser save), download the methods document (the recipe that rebuilds the
// artifact), save either to the session's project files, and
// export-to-workbench (drop a converted copy into a workbench project's
// exports directory). Stateless — each call returns a
// result object so callers can manage their own stages and error UI.
//
// The workbench project id is supplied per call (selected in the export
// dialog) because RWB workbench projects are an independent identifier
// space from AI projects — the conversation has no implicit workbench.
//
// With a pin in scope, downloads go through the pin instead of the session,
// for a reader who can't open the session the artifact is in. The pin's
// routes need no artifact id, so a surface that knows only the pin may leave
// it out; it is then absent from analytics too.
export function useArtifactActions(artifactId: string | undefined, artifactType: ArtifactType) {
  const { conversationId, pin } = useConversationContext();
  const source = downloadSource(conversationId, pin, artifactId);
  const artifact = {
    ...(artifactId ? { artifact_id: artifactId } : {}),
    artifact_type: artifactType,
  };

  const download = async (format: ConvertAndCopyFormat): Promise<DownloadResult> => {
    if (!source) {
      return { ok: false, error: "Downloads are not available in this view." };
    }
    try {
      const result = await apiMutate<ArtifactDownloadResult>(source.downloadPath, "POST", {
        format,
      });
      // Pull the bytes ourselves so the browser uses our filename rather
      // than whatever the presigned URL hands back via Content-Disposition.
      const res = await fetch(result.download_url);
      if (!res.ok) throw new Error(`Download failed with status ${res.status}`);
      saveBlob(await res.blob(), result.file_name);
      trackNextgenChatEvent(NEXTGEN_CHAT_EVENT.ARTIFACT_DOWNLOADED, source.context, {
        ...artifact,
        format,
        ...source.properties,
      });
      return { ok: true };
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      trackNextgenChatEvent(NEXTGEN_CHAT_EVENT.ARTIFACT_DOWNLOAD_FAILED, source.context, {
        ...artifact,
        format,
        error,
        ...source.properties,
      });
      return { ok: false, error };
    }
  };

  const downloadMethodsDocument = async (
    documentFormat: MethodsDocumentFormat
  ): Promise<DownloadResult> => {
    if (!source) {
      return { ok: false, error: "Downloads are not available in this view." };
    }
    const format = methodsValueFor(documentFormat);
    try {
      const methods = await apiClient<ArtifactMethodsDocument>(
        source.methodsDocumentPath(documentFormat)
      );
      saveBlob(
        new Blob([methods.content], { type: METHODS_CONTENT_TYPES[documentFormat] }),
        methods.file_name
      );
      trackNextgenChatEvent(NEXTGEN_CHAT_EVENT.ARTIFACT_DOWNLOADED, source.context, {
        ...artifact,
        format,
        ...source.properties,
      });
      return { ok: true };
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      trackNextgenChatEvent(NEXTGEN_CHAT_EVENT.ARTIFACT_DOWNLOAD_FAILED, source.context, {
        ...artifact,
        format,
        error,
        ...source.properties,
      });
      return { ok: false, error };
    }
  };

  const exportToWorkbench = async (
    format: ConvertAndCopyFormat,
    workbenchProjectId: number
  ): Promise<ExportResult> => {
    if (!conversationId || !artifactId) {
      return { ok: false, error: "Exports are not available in this view." };
    }
    try {
      const result = await apiMutate<ArtifactExportResult>(
        artifactExportPath(conversationId, artifactId),
        "POST",
        { project_id: workbenchProjectId, format }
      );
      trackNextgenChatEvent(
        NEXTGEN_CHAT_EVENT.ARTIFACT_EXPORT_COMPLETED,
        { conversationId },
        {
          artifact_id: artifactId,
          artifact_type: artifactType,
          format,
          workbench_project_id: workbenchProjectId,
        }
      );
      return { ok: true, result };
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      trackNextgenChatEvent(
        NEXTGEN_CHAT_EVENT.ARTIFACT_EXPORT_FAILED,
        { conversationId },
        {
          artifact_id: artifactId,
          artifact_type: artifactType,
          format,
          workbench_project_id: workbenchProjectId,
          error,
        }
      );
      return { ok: false, error };
    }
  };

  // The server resolves the project from the conversation and writes as the
  // caller, so a member without write access gets an error, not a file.
  const save = async (
    format: string,
    request: (conversationId: string, artifactId: string) => Promise<ProjectFile>
  ): Promise<SaveResult> => {
    if (!conversationId || !artifactId) {
      return { ok: false, error: "Saving is not available in this view." };
    }
    try {
      const file = await request(conversationId, artifactId);
      trackNextgenChatEvent(
        NEXTGEN_CHAT_EVENT.ARTIFACT_SAVED_TO_PROJECT,
        { conversationId },
        { artifact_id: artifactId, artifact_type: artifactType, format }
      );
      return { ok: true, file };
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      trackNextgenChatEvent(
        NEXTGEN_CHAT_EVENT.ARTIFACT_SAVE_TO_PROJECT_FAILED,
        { conversationId },
        { artifact_id: artifactId, artifact_type: artifactType, format, error }
      );
      return { ok: false, error };
    }
  };

  const saveToProject = (format: ConvertAndCopyFormat) =>
    save(format, (conversation, artifact) =>
      apiMutate<ProjectFile>(artifactProjectFilesPath(conversation, artifact), "POST", { format })
    );

  const saveMethodsDocumentToProject = (documentFormat: MethodsDocumentFormat) =>
    save(methodsValueFor(documentFormat), (conversation, artifact) =>
      apiMutate<ProjectFile>(
        artifactMethodsProjectFilesPath(conversation, artifact, documentFormat),
        "POST"
      )
    );

  return {
    download,
    downloadMethodsDocument,
    saveToProject,
    saveMethodsDocumentToProject,
    exportToWorkbench,
  };
}

interface DownloadSource {
  downloadPath: string;
  methodsDocumentPath: (format: MethodsDocumentFormat) => string;
  context: NextgenChatEventContext;
  properties: Record<string, string>;
}

// Where an artifact downloads from: its pin when one is in scope, else its
// session, else nowhere (a share-link view).
function downloadSource(
  conversationId: string | null,
  pin: PinRef | null,
  artifactId: string | undefined
): DownloadSource | null {
  if (pin) {
    return {
      downloadPath: pinnedArtifactDownloadPath(pin),
      methodsDocumentPath: (format) => pinnedArtifactMethodsDocumentPath(pin, format),
      context: { conversationId: pin.conversationId, projectId: pin.projectId },
      properties: { pin_id: pin.pinId },
    };
  }
  if (!conversationId || !artifactId) return null;
  return {
    downloadPath: artifactDownloadPath(conversationId, artifactId),
    methodsDocumentPath: (format) =>
      artifactMethodsDocumentPath(conversationId, artifactId, format),
    context: { conversationId },
    properties: {},
  };
}
